import { alpha } from "@mui/material";
import type { Theme } from "@mui/material";

import type { BrandThemeLike } from "./brandGradient";

/**
 * The one source for the brand purple used as FOREGROUND — link text, accent text, an
 * accent icon. NOT for backgrounds, borders or tints; those keep reading `brand[500]` /
 * `brand[600]` directly (see `navChrome.ts` and `fieldChrome.ts`, which own those).
 *
 * ## Why a helper and not a palette value
 *
 * The `brand` ramp in `palette.ts` is mode-INDEPENDENT: one object, spread into both the
 * light and the dark theme, so `brand[600]` is `#7F48DF` in both. That is correct for a
 * TINT — `alpha(brand[500], 0.12)` composites against whatever surface it lands on and
 * behaves in both modes. It is wrong for TEXT, because text contrast is a relationship
 * between the ink and the surface, and the two surfaces are at opposite ends of the range.
 *
 * Measured, `brand[600]` (`#7F48DF`) as text:
 *
 *              page                     card / paper
 *   light      4.91:1  (#f4f5f5)        5.17:1  (#fafafa)     AA pass
 *   dark       3.69:1  (#09090b)        3.55:1  (#0f0f12)     AA FAIL
 *
 * So the same token that is comfortable in light is under WCAG AA's 4.5:1 for body text in
 * dark, which is what "the purple reads too heavy against the near-black" is describing:
 * a mid-dark purple on a near-black page has too little luminance separation to sit
 * forward of it, so it reads as a dense block rather than as text.
 *
 * Dark therefore takes `brand[400]` (`#AA94F1`) — a lighter step of the SAME ramp, not a
 * new colour:
 *
 *              page                     card / paper
 *   dark       7.78:1  (#09090b)        7.49:1  (#0f0f12)     AA + AAA pass
 *
 * `brand[500]` (`#8F68EA`) was the more conservative single step and is rejected: it
 * measures 5.06:1 on the page but **4.86:1** on `background.paper`, which clears AA by
 * 0.36 and would fail outright the moment a link lands on any surface a shade lighter
 * than paper. A token this widely consumed should not sit that close to the line.
 *
 * LIGHT IS DELIBERATELY UNCHANGED. It measures fine and the complaint was dark-only;
 * moving light to `brand[400]` would be actively harmful — `#AA94F1` on `#fafafa` is
 * **2.45:1**, a clear AA failure. This is the same trap `navRestText` documents for
 * `text.secondary`, which is why that helper exists in the same shape a file away.
 */
/**
 * Takes `BrandThemeLike` rather than `Theme` — see that type's docblock. This monorepo
 * resolves `@mui/material` twice (react 18 for `apps/*`, react 19 for `packages/ui`), so a
 * `Theme` parameter here makes every app-side call site a type error for a value that is
 * identical at runtime. Widening is not a loosening: this function reads `palette.mode` and
 * `palette.brand`, which is exactly what the structural type states.
 */
export const brandText = (theme: BrandThemeLike): string => {
  const brand = (theme.palette as any).brand
  return theme.palette.mode === "light"
    ? brand?.[600] || "#7F48DF"
    : brand?.[400] || "#AA94F1"
}


/* ──────────────────────────────────────────────────────────────────────────────────────
 * The rest of this file arrived from a CONCURRENT branch that derived `brandText` again
 * under the name `brandLink`, from the same measurements and with the same rejection of
 * `brand[500]`. Two sessions, one conclusion — so the duplicate was deleted rather than
 * kept alongside, and its hover / target-tint / underline partners folded in here where
 * the token they belong to already lives (§36).
 * ────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Hover state for a brand link — one step FURTHER from the background in each mode
 * (`brand[700]` light, `brand[300]` dark), so hover deepens contrast rather than
 * eroding it.
 *
 * Exported alongside `brandText` deliberately: a colour token with no hover partner gets
 * a hover invented at each call site, and an invented one is as likely to move toward the
 * background as away from it. Both of these pass AA comfortably (6.27 light, 11.25 dark).
 */
export const brandTextHover = (theme: Theme): string =>
  theme.palette.mode === "light"
    ? (theme.palette as any).brand?.[700] || "#6F36CB"
    : (theme.palette as any).brand?.[300] || "#C7BBF7";

/**
 * The wash on a DEEP-LINK TARGET — the entry a `#anchor` just scrolled you to.
 *
 * A background tint, never coloured text. Recolouring the landed term purple would
 * reintroduce the very failure this file fixes, and it would also make the one entry you
 * were sent to the one entry that is hardest to read.
 *
 * Matches the selectable-card hover treatment (§34: tint = "considering") rather than a
 * brand BORDER, which §34 reserves for "chosen" and which — as a left accent bar — has
 * already been tried on this surface and rejected on sight.
 *
 * Light is 6%, the same figure as `brand['600-6%']`. Dark is 14%, and the asymmetry is
 * required, not sloppy: a 6% purple wash over #09090b is a ~1.02:1 step, i.e. invisible.
 * The tint has to be strong enough to locate the entry and quiet enough not to look like
 * a selection — both modes are tuned to roughly the same PERCEIVED step, not the same
 * alpha.
 */
export const brandTargetTint = (theme: Theme): string =>
  alpha(
    (theme.palette as any).brand?.[500] || "#8F68EA",
    theme.palette.mode === "light" ? 0.06 : 0.14,
  );

/**
 * Underline offset + thickness for a persistently-underlined brand link.
 *
 * 3px clears the descenders on the lowercase terms this is used for ("provenance",
 * "tag-family"), so the rule reads as an underline rather than a strikethrough of the
 * word's tail. 1px keeps it quiet on a dense reference page where a dozen of these can
 * appear in one viewport.
 */
export const LINK_UNDERLINE_OFFSET = 3;
export const LINK_UNDERLINE_THICKNESS = 1;
